import { authClient } from 'auth'
import { getErrorMessage } from 'lib/getErrorMessage'

export interface Params {
  [key: string]: string
}

export interface IHttpResponse<T> extends Response {
  parsedBody?: T
}

const getBearer = async () => {
  let code

  try {
    code = await authClient.getAPIToken()
  } catch {
    code = false
  }

  return code
}

const parseHeaders = async (headersInit?: HeadersInit) => {
  const bearer = await getBearer()

  const headers = {
    ...(bearer && { Authorization: `Bearer ${bearer}` }),
    ...headersInit,
  } as any

  // Remove Content-Type if it's equals to 'multipart/form-data'. In that way te browser
  // will add/complete the correct Content-Type for forms with files.
  if (headers['Content-Type'] === 'multipart/form-data') {
    delete headers['Content-Type']
  } else if (!headers['Content-Type']) {
    headers['Content-Type'] = 'application/json; charset=utf-8'
  }

  return headers
}

async function http<T>(request: RequestInfo): Promise<IHttpResponse<T>> {
  let response: IHttpResponse<T>

  try {
    response = await fetch(request)
  } catch (e) {
    response = { ok: false } as IHttpResponse<T>
    console.error(getErrorMessage(e))
  }

  try {
    response.parsedBody = await response.json()
  } catch {
    if (response.ok) {
      return response
    }
  }

  if (!response.ok && !response.parsedBody) {
    response = { ok: false, statusText: response.statusText } as IHttpResponse<T>
  }

  return response
}

export function removeProp(params: Params, prop: string) {
  const duplicatedParams = { ...params }

  delete duplicatedParams[prop]

  return duplicatedParams
}

export async function get<T>(
  path: string,
  params?: Record<string, string> | null,
  conf?: RequestInit,
): Promise<IHttpResponse<T>> {
  const args = {
    method: 'GET',
    ...conf,
    headers: await parseHeaders(conf && conf.headers),
  }

  const urlParams = params ? `?${new URLSearchParams(params).toString()}` : ''

  return await http<T>(new Request(path + urlParams, args))
}

export async function del<T>(
  path: string,
  params?: Record<string, string> | null,
  conf?: RequestInit,
): Promise<IHttpResponse<T>> {
  const args = {
    method: 'DELETE',
    ...conf,
    headers: await parseHeaders(conf && conf.headers),
  }

  const urlParams = params ? `?${new URLSearchParams(params).toString()}` : ''

  return await http<T>(new Request(path + urlParams, args))
}

export async function post<T>(
  path: string,
  params?: Record<string, string> | null,
  conf?: RequestInit,
): Promise<IHttpResponse<T>> {
  const args = {
    method: 'POST',
    body: JSON.stringify(params),
    ...conf,
    headers: await parseHeaders(conf && conf.headers),
  }

  return await http<T>(new Request(path, args))
}

export async function put<T>(
  path: string,
  params?: Record<string, string> | null,
  conf?: RequestInit,
): Promise<IHttpResponse<T>> {
  const args = {
    method: 'PUT',
    body: JSON.stringify(params),
    ...conf,
    headers: await parseHeaders(conf && conf.headers),
  }

  return await http<T>(new Request(path, args))
}
